/**
 * Real time → sim ticks, with input-triggered early ticks.
 *
 * The sim runs at a fixed 50 ms tick. Waiting for the next natural tick adds 0–50 ms to every
 * key press, so when input arrives and at least `minEarlyMs` of the current tick has elapsed,
 * the next tick runs right away and the accumulator carries the difference as debt (see
 * FixedStepAccumulator.stepEarly). Presses sooner than that are scheduled for the moment it
 * becomes allowed, so a command waits at most `minEarlyMs`.
 *
 * Determinism: the sim only sees `step(commands)` calls, never wall-clock time. Early ticks
 * change *when* in real time a tick runs, not what it computes, so a run is fully described by
 * which commands went into which tick. `CommandLog` records exactly that, and `replayLog`
 * reproduces the run.
 */
import type { Command, Sim, SimEvent } from '@keystone/sim';
import { FixedStepAccumulator } from './fixedStep';

/** Earliest point (ms into the current tick) at which input may trigger the next tick. */
export const EARLY_TICK_MIN_MS = 20;

export interface Scheduler {
  set(fn: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface TickDriverOptions {
  stepMs: number;
  /** Real-time clock (performance.now in the browser). */
  now(): number;
  minEarlyMs?: number;
  /** Timer for presses that arrive too early in a tick (omit = wait for the frame loop). */
  scheduler?: Scheduler;
}

export class TickDriver {
  readonly acc: FixedStepAccumulator;
  readonly minEarlyMs: number;
  /** Ticks run early because of input (diagnostics). */
  earlyTicks = 0;
  private last: number | null = null;
  private timer: unknown = null;
  private wants: (() => boolean) | null = null;

  constructor(
    private readonly runTick: () => void,
    private readonly opts: TickDriverOptions,
  ) {
    this.acc = new FixedStepAccumulator({ stepMs: opts.stepMs });
    this.minEarlyMs = opts.minEarlyMs ?? EARLY_TICK_MIN_MS;
  }

  /** Bring the sim up to real time `now`, running every whole tick that is due. Returns ticks run. */
  advanceTo(now: number): number {
    if (this.last === null) {
      this.last = now;
      return 0;
    }
    const dt = now - this.last;
    if (!(dt > 0)) return 0;
    this.last = now;
    return this.acc.advance(dt, this.runTick);
  }

  /**
   * Input arrived. If `hasWork()` (commands are waiting for the sim), run the next tick now
   * when allowed, else schedule it for the earliest allowed moment. True if a tick ran now.
   */
  input(hasWork: () => boolean): boolean {
    this.advanceTo(this.opts.now());
    if (!hasWork()) return false;
    if (this.acc.stepEarly(this.minEarlyMs, this.runTick)) {
      this.earlyTicks++;
      return true;
    }
    this.wants = hasWork;
    const wait = this.acc.msUntilEarly(this.minEarlyMs);
    if (this.timer === null && Number.isFinite(wait) && this.opts.scheduler) {
      this.timer = this.opts.scheduler.set(() => {
        this.timer = null;
        const w = this.wants;
        this.wants = null;
        if (w) this.input(w);
      }, wait);
    }
    return false;
  }

  /** Sim ms since the latest tick (negative while the sim runs ahead after an early tick). */
  get sinceStepMs(): number {
    return this.acc.sinceStepMs;
  }

  dispose(): void {
    if (this.timer !== null) this.opts.scheduler?.clear(this.timer);
    this.timer = null;
    this.wants = null;
  }
}

export interface LoggedTick {
  /** world.tick after the step that applied these commands. */
  tick: number;
  commands: Command[];
}

/** Which commands went into which tick (only ticks with commands are stored). */
export class CommandLog {
  readonly entries: LoggedTick[] = [];

  record(tick: number, commands: readonly Command[]): void {
    if (commands.length === 0) return;
    this.entries.push({ tick, commands: structuredClone(commands as Command[]) });
  }
}

/**
 * Re-run a recorded log on a fresh sim for `ticks` steps. `extra(tick)` supplies commands that
 * were not logged (e.g. bots, which are deterministic given the world); they go after the
 * logged ones for that tick, matching the live order (player first, then bots). Returns each
 * step's events.
 */
export function replayLog(sim: Sim, log: readonly LoggedTick[], ticks: number, extra?: (tick: number) => Command[]): SimEvent[][] {
  const byTick = new Map<number, Command[]>();
  for (const e of log) byTick.set(e.tick, [...(byTick.get(e.tick) ?? []), ...e.commands]);
  const out: SimEvent[][] = [];
  for (let i = 0; i < ticks; i++) {
    const tick = sim.getWorld().tick + 1;
    const cmds = [...(byTick.get(tick) ?? []), ...(extra?.(tick) ?? [])];
    out.push(sim.step(cmds));
  }
  return out;
}
